import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiPhone,
    FiAlertTriangle,
    FiPieChart,
    FiCheckCircle,
    FiAlertCircle,
    FiClock,
    FiDownload,
} from 'react-icons/fi';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
} from 'chart.js';
import { useProfiles } from '../../hooks/useProfiles';
import {
    PageHeader,
    MetricCard,
    StatusBadge,
    EmptyState,
    SkeletonMetricCard,
    SkeletonChart,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import Avatar from '../../theme/components/Avatar';
import { telLink } from '../../utils/format';
import { APP_NAME } from '../../config';
import { buildPdfReport } from '../../utils/pdfReport';
import './VaccineSummaries.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale);

const calculateAgeInMonths = (dob) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
    months -= birthDate.getMonth();
    months += today.getMonth();
    if (today.getDate() < birthDate.getDate()) months -= 1;
    return months < 0 ? 0 : months;
};

const VaccineSummaries = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { data: apiData, loading, error } = useProfiles();
    const childrenData = useMemo(() => apiData || [], [apiData]);

    // Count children once per bucket they have doses in. A child with
    // 2 upcoming + 3 due counts in BOTH Upcoming and Due (one per bucket).
    // Uses child.vaccinationCounts.{upcoming,due,overdue} from BE — the
    // same source the action-queue pills render below. Falls back to
    // vaccinationStatus only when vaccinationCounts is absent (older
    // payloads) so the report still renders. ponytail: a child can land
    // in multiple buckets here, so bucket totals exceed childrenData.length;
    // the chart legend still reads correctly because Pie slices are
    // sized by raw count, not percentage of total children.
    const stats = useMemo(() => {
        const s = { 'Up to Date': 0, Upcoming: 0, Due: 0, Overdue: 0 };
        childrenData.forEach((child) => {
            const c = child.vaccinationCounts;
            if (c && typeof c === 'object') {
                const pending = (c.upcoming || 0) + (c.due || 0) + (c.overdue || 0);
                if (pending === 0) {
                    s['Up to Date'] += 1;
                    return;
                }
                if (c.upcoming > 0) s.Upcoming += 1;
                if (c.due > 0) s.Due += 1;
                if (c.overdue > 0) s.Overdue += 1;
                return;
            }
            // Legacy fallback: single vaccinationStatus string per child.
            const raw = child.vaccinationStatus || 'Completed';
            const key = raw === 'Completed' ? 'Up to Date' : raw;
            if (s[key] !== undefined) s[key] += 1;
            else s['Up to Date'] += 1;
        });
        return s;
    }, [childrenData]);

    const chartData = useMemo(
        () => ({
            labels: ['Up to Date', 'Upcoming', 'Due', 'Overdue'],
            datasets: [
                {
                    data: [stats['Up to Date'], stats.Upcoming, stats.Due, stats.Overdue],
                    backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'],
                    borderWidth: 0,
                    hoverOffset: 8,
                },
            ],
        }),
        [stats],
    );

    const actionQueue = useMemo(
        () =>
            childrenData.filter((c) => {
                // Use the same vaccinationCounts source as the MetricCards
                // so the queue length always equals the sum of Due + Overdue
                // tiles. PDF body column still reads c.vaccinationStatus
                // (a per-child priority-resolved statusType, untouched here).
                // Legacy fallback: if the new vaccinationCounts field is
                // absent on an old payload, fall back to the legacy string
                // so the queue stays consistent with the stats fallback at
                // lines 82-86.
                const v = c.vaccinationCounts;
                if (v) return v.due > 0 || v.overdue > 0;
                return c.vaccinationStatus === 'Due' || c.vaccinationStatus === 'Overdue';
            }),
        [childrenData],
    );

    const generatePDF = () => {
        const stamp = new Date().toISOString().split('T')[0];
        buildPdfReport({
            title: 'Vaccination Status Summary Report',
            subtitle: `${APP_NAME} · Total Children: ${childrenData.length}`,
            filters: [`Vaccination view: all registered (${childrenData.length} assessed)`],
            sections: [
                {
                    heading: 'Status Distribution',
                    head: ['Count', 'Percentage'],
                    body: Object.entries(stats).map(([label, count]) => [
                        count,
                        `${((count / Math.max(1, childrenData.length)) * 100).toFixed(1)}%`,
                    ]),
                    accent: [79, 70, 229],
                },
                {
                    heading: 'Action Queue (Due & Overdue)',
                    head: [
                        'Reg No',
                        'Child Name',
                        'Age (Mos)',
                        'Last/Next Vaccine',
                        "Mother's Name",
                        'Contact',
                    ],
                    body: actionQueue.map((child) => [
                        child.childNo,
                        child.name,
                        calculateAgeInMonths(child.dob),
                        child.vaccinationStatus === 'Completed'
                            ? 'Up to Date'
                            : child.vaccinationStatus || '-',
                        child.mothersName || '-',
                        child.contactNumber || '-',
                    ]),
                    accent: [239, 68, 68],
                },
            ],
            fileName: `Vaccination_Status_Report_${stamp}.pdf`,
            toast,
        });
    };

    if (loading) {
        return (
            <div className="vs-wrapper">
                <PageHeader
                    title="Vaccination Status Report"
                    subtitle="Loading clinic data…"
                    icon={FiPieChart}
                />
                <div className="vs-body">
                    <div className="vs-metrics">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <SkeletonMetricCard key={i} />
                        ))}
                    </div>
                    <div className="vs-grid">
                        <SkeletonChart height={280} />
                        <div className="vs-action-card">
                            <div className="vs-action-card__title">Action Queue</div>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="vs-action-placeholder" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="vs-wrapper">
                <EmptyState
                    illustration="error"
                    title="Couldn't load report"
                    description={error}
                    action={
                        <PageButton type="button" variant="primary" onClick={() => window.location.reload()}>
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="vs-wrapper">
            <PageHeader
                title="Vaccination Status Report"
                subtitle="Comprehensive overview of immunization coverage and required follow-ups."
                icon={FiPieChart}
                actions={
                    <>
                        <PageButton type="button" variant="ghost" startIcon={<FiArrowLeft size={16} />} onClick={() => navigate('/reports')}>
                            Back
                        </PageButton>
                        <PageButton
                            type="button"
                            variant="primary"
                            startIcon={<FiDownload size={16} />}
                            onClick={generatePDF}
                            disabled={childrenData.length === 0}
                        >
                            Export PDF
                        </PageButton>
                    </>
                }
            />

            <div className="vs-body">
                <div className="vs-metrics">
                    <MetricCard
                        label="Up to Date"
                        value={stats['Up to Date']}
                        accent="success"
                        icon={FiCheckCircle}
                    />
                    <MetricCard label="Upcoming" value={stats.Upcoming} accent="info" icon={FiClock} />
                    <MetricCard label="Due" value={stats.Due} accent="warning" icon={FiAlertCircle} />
                    <MetricCard
                        label="Overdue"
                        value={stats.Overdue}
                        accent="error"
                        icon={FiAlertTriangle}
                    />
                </div>

                <div className="vs-grid">
                    <div className="vs-panel">
                        <div className="vs-panel__header">
                            <h3>Coverage Distribution</h3>
                            <span className="vs-panel__subtitle">
                                {childrenData.length} children total
                            </span>
                        </div>
                        <div className="vs-chart-body">
                            <Pie data={chartData} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>

                    <div className="vs-action-card">
                        <div className="vs-action-card__header">
                            <h3>
                                <FiAlertTriangle size={16} /> Action Queue
                            </h3>
                            <StatusBadge severity="danger" dot>
                                {actionQueue.length} priority
                            </StatusBadge>
                        </div>
                        <div className="vs-action-list">
                            {actionQueue.length > 0 ? (
                                actionQueue.map((child) => {
                                    // Same per-bucket pill stack as Profiles.jsx
                                    // vaccination column — counts come from
                                    // child.vaccinationCounts.{overdue,due,upcoming}.
                                    const counts = child.vaccinationCounts || {};
                                    // Action queue is the follow-up bucket — only Due +
                                    // Overdue badges belong here, even if the child also
                                    // has Upcoming doses. Upcoming lives on the Profiles
                                    // pill stack and the Reminders filter, not here.
                                    const vaccPills = [
                                        { key: 'overdue',  count: counts.overdue || 0,  label: 'Overdue',  severity: 'danger'  },
                                        { key: 'due',      count: counts.due || 0,      label: 'Due',      severity: 'warning' },
                                    ].filter((p) => p.count > 0);
                                    return (
                                        <div key={child.id} className="vs-action-item">
                                            <Avatar name={child.name} id={child.id || child.childNo} size={36} />
                                            <div className="vs-action-item__info">
                                                <strong>{child.name}</strong>
                                                <span className="vs-action-item__sub">
                                                    {vaccPills.map((p) => (
                                                        <StatusBadge key={p.key} severity={p.severity}>
                                                            {p.label} {p.count}
                                                        </StatusBadge>
                                                    ))}
                                                    <span>· {calculateAgeInMonths(child.dob)} months</span>
                                                </span>
                                            </div>
                                        <div className="vs-action-item__contact">
                                            <div className="vs-action-item__mother">{child.mothersName || 'Mother'}</div>
                                            {child.contactNumber ? (
                                                <a href={telLink(child.contactNumber)} className="vs-action-item__call">
                                                    <FiPhone size={12} /> {child.contactNumber}
                                                </a>
                                            ) : (
                                                <span className="vs-action-item__call vs-action-item__call--muted">
                                                    <FiPhone size={12} /> No contact
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })
                            ) : (
                                <EmptyState
                                    illustration="no-data"
                                    compact
                                    title="Everyone is up to date!"
                                    description="No follow-up vaccinations are due at the moment."
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VaccineSummaries;
